/**
 * MU side of the axis-addon@1 postMessage protocol.
 *
 * When MU is iframed inside DC (or any future host), this module:
 *   - announces 'ready' once mounted
 *   - listens for select / focus / theme / viewport messages
 *   - exposes reactive runes other components can read
 *
 * When MU is loaded standalone (`window === window.parent`), this module
 * stays inert — every operation degrades to no-op so MU works as both
 * a hosted addon and a first-class standalone PWA.
 *
 * See v2/docs/ADDON_MANIFEST.md for the host-side bridge.
 */

import { acceptHostBearer, type McoIdentity } from './mcoAuth.svelte'

const PROTO = 'axis-addon@1'

interface ProtoState {
  isHosted: boolean
  hostOrigin: string | null
  selectedId: number | null
  focusTopic: string | null
  themeMode: 'dark' | 'light'
  viewport: { w: number; h: number }
}

export const proto = $state<ProtoState>({
  isHosted: typeof window !== 'undefined' && window !== window.parent,
  hostOrigin: null,
  selectedId: null,
  focusTopic: null,
  themeMode: 'dark',
  viewport: { w: 0, h: 0 },
})

function postToHost(msg: Record<string, unknown>): void {
  if (!proto.isHosted) return
  /* When hosted, the parent origin isn't known until it sends us its first
   * message (browsers rightly hide it). Until then, post to '*' for the
   * 'ready' handshake only — once the host responds, hostOrigin pins. */
  const target = proto.hostOrigin ?? '*'
  window.parent.postMessage({ proto: PROTO, ...msg }, target)
}

export function announceReady(): void {
  postToHost({ type: 'ready' })
}

export function notifyHost(level: 'info' | 'warn' | 'error', text: string): void {
  postToHost({ type: 'notify', level, text })
}

export function reportSelection(id: number): void {
  postToHost({ type: 'select', id })
}

export function requestNavigate(screen: string): void {
  postToHost({ type: 'navigate', screen })
}

/* ── Listener ─────────────────────────────────────────────────── */

function applyTheme(tokens: Record<string, string> | undefined, mode: 'dark' | 'light'): void {
  proto.themeMode = mode
  document.documentElement.dataset.theme = mode
  if (!tokens) return
  const root = document.documentElement
  for (const [k, v] of Object.entries(tokens)) {
    if (k.startsWith('--')) root.style.setProperty(k, v)
  }
}

function onMessage(e: MessageEvent): void {
  const data = e.data
  if (!data || typeof data !== 'object') return
  if ((data as { proto?: string }).proto !== PROTO) return

  /* First valid message pins host origin; subsequent messages from any
   * other origin are dropped. Defends against multi-frame attacks where
   * a sibling iframe tries to spoof the host. */
  if (proto.hostOrigin == null) {
    proto.hostOrigin = e.origin
  } else if (e.origin !== proto.hostOrigin) {
    return
  }

  const msg = data as { type?: string; [k: string]: unknown }
  switch (msg.type) {
    case 'auth':
      /* Host (DC) handed us its MCO bearer. Adopt it in-memory so all
       * mu-api calls carry Authorization + the right tenant. Re-fired by
       * host whenever its bearer changes so state stays in lockstep. */
      if (typeof msg['token'] === 'string') {
        acceptHostBearer({
          token: msg['token'],
          expiresAt: typeof msg['expiresAt'] === 'string' ? msg['expiresAt'] : null,
          identity: (msg['identity'] as McoIdentity | null) ?? null,
        })
      }
      break

    case 'select':
      if (typeof msg['id'] === 'number') proto.selectedId = msg['id']
      break

    case 'focus':
      if (typeof msg['topic'] === 'string') proto.focusTopic = msg['topic']
      break

    case 'theme':
      applyTheme(
        msg['tokens'] as Record<string, string> | undefined,
        (msg['mode'] as 'dark' | 'light') ?? 'dark',
      )
      break

    case 'viewport':
      if (typeof msg['w'] === 'number' && typeof msg['h'] === 'number') {
        proto.viewport = { w: msg['w'], h: msg['h'] }
      }
      break

    case 'ping':
      postToHost({ type: 'pong', nonce: msg['nonce'] })
      break

    case 'reject':
      /* Host refused our request. Surface in dev console; v1 wires this
       * into a UI banner. */
      console.warn('[mu-app] host rejected:', msg['reason'])
      break

    default:
      break
  }
}

let started = false

export function startProtocol(): () => void {
  if (started || typeof window === 'undefined') {
    return () => { /* no-op */ }
  }
  started = true
  window.addEventListener('message', onMessage)
  /* Announce on next tick so any reactive setup runs before the host fires
   * its initial theme/viewport pushes. */
  queueMicrotask(announceReady)
  return () => {
    window.removeEventListener('message', onMessage)
    started = false
  }
}
